/**
 * "Finish this person" — the reference's Finish sheet (s-finish). Opens pre-filled (every room ticked,
 * Standard — 17 Oct 2026: Deep when the visit was booked as a deep clean — the scheduled length, one worker,
 * Completed, Routine, both switches off): a normal visit is one press of Record. Two switches, any order: Incident
 * (what happened → the incident memo) and Unit condition (the incident report → the sheet widens to 60% of the
 * screen; the button reads "Record & send the report"). The list's own validation is said in plain words before
 * anything is sent. After photos go to the visit's folder as each is taken (15 Oct 2026); the report's photos stay on
 * the device until Record. 17 Oct 2026: a photo is said as added only when the server took it — one that did not
 * upload is said plainly, stays on the sheet with every other answer, and is tried again (Try again, or Record).
 * The completion goes to Queue 3 and the processor's answer shows on the row (the tracker).
 *
 * Modes: record (a new close-out), edit (after close-out: inside the window nothing has gone anywhere;
 * after it, a new report version), resubmit (a completion returned for correction).
 */
import { useEffect, useMemo, useRef, useState, type Dispatch, type RefObject, type SetStateAction } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { CloseoutSubmitResponse, PhotoUploadResponse } from '@shared/api-types';
import type { CloseoutMarker, FinishConfig } from '@shared/finish';
import {
  COMPLETION_FOLLOW_UP_CATEGORIES,
  CONDITION_LEVELS,
  DURATION_STEPS,
  MAX_WORKERS,
  PRIORITIES,
  SERVICE_OUTCOMES,
  defaultAssessment,
  defaultCloseout,
  levelFromSeverity,
  validateCloseout,
  type CloseoutForm,
  type Invalid,
} from '@shared/closeout';
import { newFormSession } from '@shared/envelopes/queue1';
import { apiFetch, ApiRequestError } from '@/lib/api';
import { useOfflineQueue } from '@/features/offline/OfflineQueueProvider';
import { isNetworkFailure } from '@/features/offline/queue';
import { useTx } from '@/lib/tx';
import { useDirtyFlag } from '@/lib/dirty';
import { pressWords } from '@/lib/press-words';
import { formatDayKey } from '@/lib/dates';
import { useAnnounce } from '@/components/Announcer';
import { ChangeInSettings } from '@/components/ChangeInSettings';
import { Icon } from '@/components/Icons';
import { useToast } from '@/components/ui/use-toast';
import { Button } from '@/components/ui/button';
import { SheetContent } from '@/components/ui/sheet';
import { IncidentReport } from './IncidentReport';
import { PhotoPicker, reportClientProblem, type PhotoItem } from './Photos';
import { createPhotoSender, rememberTakenOff, takenOffPaths, type PhotoSender } from './photoSender';
import { BeforePhotos } from './BeforePhotos';
import { WhatIs, YesNo } from './bits';

/** 'photos' (Eli, 16 Oct 2026): a sent close-out stays as it is; anyone on the team adds photos to the visit. */
export type FinishMode = 'record' | 'edit' | 'resubmit' | 'photos';

export interface FinishTarget {
  appointmentId: string;
  unit: string;
  tenantName: string;
  serviceDateKey: string;
  /** The position's clock time on the day plan (a label). */
  time?: string | null;
  /** 1.7.8: the start the office chose for this visit today (minutes after midnight) — Start offers it first. Optional. */
  chosenStart?: number | null;
}

interface CloseoutRead {
  marker: CloseoutMarker;
  form: CloseoutForm;
  photoRelease: { onFile: boolean; date: string | null };
}

/** 17 Oct 2026: photo n of this Record did not upload (an error answer, no place in the folder, or no connection). */
class PhotoNotSent extends Error {
  constructor(readonly n: number) {
    super(`Photo ${n} did not upload.`);
  }
}

/** Why the last photo send did not get through: no answer at all (no signal), or an answer that said no. */
let lastPhotoFailure: 'network' | 'server' | null = null;

/** One photo to the visit's folder: its path there once the server took it, else null (what went wrong reaches the log). */
async function sendPhoto(appointmentId: string, purpose: 'after' | 'unit-condition', blob: Blob, name: string): Promise<string | null> {
  lastPhotoFailure = null;
  try {
    const fd = new FormData();
    fd.set('appointmentId', appointmentId);
    fd.set('purpose', purpose);
    fd.set('file', blob, name);
    const res = await fetch('/api/documents', { method: 'POST', body: fd, credentials: 'same-origin', headers: { Accept: 'application/json' } });
    const body = (await res.json().catch(() => null)) as (PhotoUploadResponse & { message?: string }) | null;
    if (res.ok && body?.path) return body.path;
    lastPhotoFailure = 'server';
    reportClientProblem('photo-send', { purpose, status: res.status, message: body?.message ?? '' });
  } catch (err) {
    lastPhotoFailure = 'network';
    reportClientProblem('photo-send', { purpose, message: err instanceof Error ? err.message : String(err) });
  }
  return null;
}

const durationLabel = (t: (k: string, o?: Record<string, unknown>) => string, m: number) => {
  const h = Math.floor(m / 60);
  const r = m % 60;
  if (!h) return t('finish.minutesOnly', { m: r });
  if (!r) return t('finish.hoursOnly', { count: h });
  return t('finish.hours', { h, m: r });
};

export function FinishSheet({
  target,
  mode,
  completionId,
  returnNotes,
  onDone,
  returnFocusTo,
}: {
  target: FinishTarget;
  mode: FinishMode;
  completionId?: string | null;
  returnNotes?: string | null;
  onDone: () => void;
  returnFocusTo?: RefObject<HTMLElement | null>;
}) {
  const { t, i18n } = useTranslation();
  const announce = useAnnounce();
  const toast = useToast();
  const tx = useTx();
  const offline = useOfflineQueue();
  const lang: 'en' | 'es' = i18n.language.startsWith('es') ? 'es' : 'en';
  const session = useMemo(() => newFormSession(), []);

  const cfgQ = useQuery({
    queryKey: ['finish-config', target.appointmentId],
    queryFn: () => apiFetch<FinishConfig>(`/api/finish/config?appointmentId=${encodeURIComponent(target.appointmentId)}`),
    staleTime: 0,
  });
  const prevQ = useQuery({
    queryKey: ['closeout', target.appointmentId],
    queryFn: () => apiFetch<CloseoutRead>(`/api/closeouts/${encodeURIComponent(target.appointmentId)}`),
    enabled: mode === 'edit' || mode === 'resubmit',
    retry: false,
  });
  const cfg = cfgQ.data;
  const prev = prevQ.data;

  const [form, setForm] = useState<CloseoutForm | null>(null);
  const [visitPhotos, setVisitPhotos] = useState<PhotoItem[]>([]);
  const [reportPhotos, setReportPhotos] = useState<PhotoItem[]>([]);
  const [invalid, setInvalid] = useState<Invalid[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const problems = useRef<HTMLDivElement>(null);
  /**
   * 18 Oct 2026 (Eli: one photo, straightened, then Record made three files): every send goes through one sender per
   * folder, which never sends the same photo twice; Record reads the list as it is now (these refs), not as it was
   * when the sheet last drew.
   */
  const afterSender = useMemo(() => createPhotoSender((blob, name) => sendPhoto(target.appointmentId, 'after', blob, name)), [target.appointmentId]);
  const reportSender = useMemo(() => createPhotoSender((blob, name) => sendPhoto(target.appointmentId, 'unit-condition', blob, name)), [target.appointmentId]);
  const visitNow = useRef(visitPhotos);
  visitNow.current = visitPhotos;
  const reportNow = useRef(reportPhotos);
  reportNow.current = reportPhotos;
  /** The After photos change: one already in the folder that is taken off (removed, or straightened) stays off the report after a reload. */
  const changeVisitPhotos = (next: PhotoItem[]) => {
    const blobs = new Set(next.map((p) => p.blob).filter(Boolean));
    const paths = new Set(next.map((p) => p.path).filter(Boolean));
    rememberTakenOff(
      target.appointmentId,
      visitNow.current.filter((p) => p.kind === 'local' && p.path && !(p.blob ? blobs.has(p.blob) : paths.has(p.path))).map((p) => p.path!),
    );
    setVisitPhotos(next);
  };
  useDirtyFlag('finish', form !== null); // 1.6.5: a close-out being filled is never reloaded away (lib/app-update.ts)

  // Pre-fill once the questions (and, for an edit, the close-out as pressed) are here.
  useEffect(() => {
    if (form || !cfg) return;
    if ((mode === 'edit' || mode === 'resubmit') && prevQ.isLoading) return;
    const q = cfg.questions;
    const base = defaultCloseout({
      roomKeys: q.rooms.filter((r) => !r.hidden).map((r) => r.key),
      standard: Object.fromEntries(q.standard.filter((s) => !s.hidden).map((s) => [s.key, s.default])),
      scheduledMinutes: cfg.appointment.scheduledMinutes,
      formVersion: q.version,
      language: lang,
      // 17 Oct 2026: a visit booked as a deep clean opens on Deep clean (the crew can still change it)
      typeOfClean: cfg.appointment.bookedTypeOfClean,
    });
    if (prev?.form) {
      setForm({ ...prev.form, formVersion: prev.form.formVersion || q.version, language: lang });
      setVisitPhotos(prev.form.photos.map((p, i) => ({ id: `f${i}`, kind: 'filed', path: p })));
      setReportPhotos((prev.form.unitCondition?.photos ?? []).map((p, i) => ({ id: `g${i}`, kind: 'filed', path: p })));
    } else {
      setForm(base);
      // 15 Oct 2026: After photos already sent (the sheet was closed, or the phone reloaded the page) are kept
      // 18 Oct 2026: …except one taken off the sheet in this tab (removed, or replaced by its straightened version)
      const off = takenOffPaths(target.appointmentId);
      const filed = (cfg.afterPhotos ?? []).filter((p) => !off.has(p.path));
      if (filed.length) setVisitPhotos(filed.map((p, i) => ({ id: `a${i}`, kind: 'filed', path: p.path, url: `/api/finish/photo?appointmentId=${encodeURIComponent(target.appointmentId)}&path=${encodeURIComponent(p.path)}` })));
    }
  }, [cfg, prev, prevQ.isLoading, mode, form, lang, target.appointmentId]);

  /**
   * 15 Oct 2026 (the crew's After photos "never appeared"): each After photo is sent as soon as it is taken, so a
   * phone that reloads the page while the camera is open, or a sheet closed by mistake, loses nothing.
   * 17 Oct 2026: it is said as added only when the server took it. One that did not upload is said plainly, stays on
   * the sheet with every other answer, and goes again with Try again (or with Record).
   */
  const sending = useRef(false);
  /** After photos that did not upload and have not been taken since: said on the sheet, with Try again. */
  const [notUploaded, setNotUploaded] = useState<ReadonlySet<string>>(() => new Set());
  /** …of those, the ones waiting for Try again (it empties this, and they are sent again). */
  const [paused, setPaused] = useState<ReadonlySet<string>>(() => new Set());
  /** What one run of sends came to, said once when it is over: taken by the server, and not uploaded. */
  const run = useRef({ ok: 0, failed: 0 });
  useEffect(() => {
    if (sending.current) return;
    const next = visitPhotos.find((p) => p.kind === 'local' && !p.path && p.blob && !paused.has(p.id));
    if (!next) {
      // the run is over: one sentence for it — "added" only for what the server took (one message: the announcer
      // keeps the last of two said at once)
      const { ok, failed } = run.current;
      run.current = { ok: 0, failed: 0 };
      if (failed) announce([t('finish.photoNotUploaded', { count: failed }), ok ? t('finish.photoAdded', { count: ok }) : ''].filter(Boolean).join(' '), 'assertive');
      else if (ok) announce(t('finish.photoAdded', { count: ok }));
      return;
    }
    const blob = next.blob!;
    // 18 Oct 2026: a photo the server already took (its answer came while the list was being changed) only takes its path
    const known = afterSender.pathOf(blob);
    if (known) {
      setVisitPhotos((all) => all.map((p) => (p.blob === blob && !p.path ? { ...p, path: known } : p)));
      return;
    }
    sending.current = true;
    void (async () => {
      const path = await afterSender.send(blob, 'photo.jpg');
      sending.current = false;
      if (path) {
        // by its Blob, not its id: a photo straightened while its original was on the way keeps no path, and is sent itself
        if (visitNow.current.some((p) => p.blob === blob)) run.current.ok++;
        else rememberTakenOff(target.appointmentId, [path]);
        setNotUploaded((s) => (s.has(next.id) ? new Set([...s].filter((x) => x !== next.id)) : s));
        setVisitPhotos((all) => all.map((p) => (p.blob === blob ? { ...p, path } : p)));
      } else {
        run.current.failed++;
        setNotUploaded((s) => new Set(s).add(next.id));
        setPaused((s) => new Set(s).add(next.id));
      }
    })();
  }, [visitPhotos, paused, target.appointmentId, afterSender, announce, t]);
  const notUploadedCount = visitPhotos.filter((p) => p.kind === 'local' && !p.path && notUploaded.has(p.id)).length;
  /** The line under the After photos while any did not upload, and its Try again. */
  const afterTrouble = notUploadedCount ? { trouble: t('finish.photoNotUploaded', { count: notUploadedCount }), onRetry: () => setPaused(new Set()) } : {};

  const title =
    mode === 'photos' ? t('finish.titlePhotos', { unit: target.unit }) : mode === 'edit' ? t('finish.titleEdit', { unit: target.unit }) : mode === 'resubmit' ? t('finish.titleResubmit', { unit: target.unit }) : t('finish.title', { unit: target.unit });
  const report = !!form?.unitCondition;

  if ((cfgQ.isError && !cfg) || !form || !cfg) {
    return (
      <SheetContent title={title} returnFocusTo={returnFocusTo} data-testid="finish-sheet">
        {cfgQ.isError ? (
          <div className="warn take" role="alert">
            <Icon name="alert" />
            <div>{t('finish.loadError', { message: (cfgQ.error as Error).message })}</div>
          </div>
        ) : (
          <p className="help">{t('finish.loading')}</p>
        )}
      </SheetContent>
    );
  }

  if (mode === 'photos') {
    const waiting = visitPhotos.some((p) => p.kind === 'local' && !p.path && !paused.has(p.id));
    return (
      <SheetContent
        title={title}
        returnFocusTo={returnFocusTo}
        data-testid="finish-sheet"
        footer={
          <Button type="button" onClick={onDone} disabled={waiting} data-testid="photos-done">
            {waiting ? t('finish.photosSending') : t('finish.photosDone')}
          </Button>
        }
      >
        <div className="finish-sections">
          <p className="help" data-testid="finish-who">
            <b>{target.unit}</b> · {t('finish.visitLine', { time: target.time ?? formatDayKey(target.serviceDateKey, i18n.language), name: target.tenantName })}
          </p>
          <div className="warn" role="note" data-testid="photos-note">
            <Icon name="info" />
            <div>{t('finish.photosNote')}</div>
          </div>
          <BeforePhotos appointmentId={target.appointmentId} photos={cfg.beforePhotos ?? []} />
          <PhotoPicker id="photo-add" testid="visit-photos" items={visitPhotos} onChange={changeVisitPhotos} legend={t('arrived.afterPhotos')} pickedWords={() => t('finish.photosSending')} {...afterTrouble} help={<span>{t('finish.photosMany')}</span>} />
        </div>
      </SheetContent>
    );
  }

  const set = <K extends keyof CloseoutForm>(k: K, v: CloseoutForm[K]) => setForm((f) => (f ? { ...f, [k]: v } : f));
  const q = cfg.questions;
  const windowMin = cfg.windowMinutes;
  const insideWindow = prev?.marker ? Date.now() <= new Date(prev.marker.windowEndsAt).getTime() : true;
  const level = form.unitCondition ? levelFromSeverity(form.unitCondition.severity) : form.level;
  const release = prev?.photoRelease ?? cfg.photoRelease;

  const setLevel = (l: CloseoutForm['level']) => {
    setForm((f) => {
      if (!f) return f;
      const next = { ...f, level: l };
      if (l === 'Elevated Cleaning Need' && !f.followUp.required) next.followUp = { ...f.followUp, required: true };
      if (l === 'Potential Health or Safety Concern' && !f.unitCondition) next.unitCondition = { ...defaultAssessment(), severity: 'Potential Health or Safety Concern' };
      return next;
    });
  };
  const setUnitCondition = (on: boolean) => {
    setForm((f) => (f ? { ...f, unitCondition: on ? (f.unitCondition ?? defaultAssessment()) : null, level: on ? f.level : f.level === 'Potential Health or Safety Concern' ? 'Routine - Nothing to Report' : f.level } : f));
    if (on) window.setTimeout(() => document.getElementById('incident-fields')?.scrollIntoView({ block: 'start', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' }), 80);
  };

  const recordLabel = mode === 'edit' ? t('finish.save') : mode === 'resubmit' ? t('finish.resubmit') : report ? t('finish.recordSend') : t('finish.record');

  /**
   * What is still on the device goes to its folder. 18 Oct 2026: first every send already on its way is waited for,
   * then the list is read as it is now; a photo the server already took (on its way when Record was pressed, or taken
   * between the sheet's last draw and the press) is never sent again — the sender answers with its path. 17 Oct 2026:
   * one the server did not take stops the send (PhotoNotSent) — the sheet keeps every answer and says so.
   */
  const upload = async (now: RefObject<PhotoItem[]>, sender: PhotoSender, offset: number, total: number, save: Dispatch<SetStateAction<PhotoItem[]>>): Promise<{ paths: string[]; sent: number }> => {
    await sender.settled();
    const out: string[] = [];
    let n = offset;
    for (const p of now.current ?? []) {
      const path0 = p.path ?? (p.blob ? sender.pathOf(p.blob) : undefined);
      if (path0) {
        out.push(path0);
        continue;
      }
      if (!p.blob) continue;
      n++;
      setBusy(t('finish.uploading', { n, of: Math.max(total, n) }));
      const path = await sender.send(p.blob, `photo-${n}.jpg`);
      if (!path) throw new PhotoNotSent(n);
      out.push(path);
      const blob = p.blob;
      save((all) => all.map((x) => (x.blob === blob ? { ...x, kind: 'filed', path } : x)));
    }
    return { paths: out, sent: n - offset };
  };

  const submit = async () => {
    const problemsNow = validateCloseout(form);
    setInvalid(problemsNow);
    setError(null);
    if (problemsNow.length) {
      announce(`${t('finish.fixThese')} ${problemsNow.map((p) => t(`finish.invalid.${p.key}`)).join(' ')}`, 'assertive');
      window.setTimeout(() => problems.current?.focus(), 30);
      return;
    }
    // 1 Nov 2026 (D-1102-CREW-3): with "The crew can save while offline" on, a new close-out that cannot be sent for want of
    // a signal is kept on this phone — the same session, the same answers, its photos — and goes when the signal returns
    const keepable = offline.enabled && mode === 'record';
    const keepOnPhone = async () => {
      const photoOf = (items: PhotoItem[] | null, sender: PhotoSender, target: 'form.photos' | 'form.unitCondition.photos', purpose: 'after' | 'unit-condition') =>
        (items ?? [])
          .filter((p) => p.blob || p.path)
          .map((p, i) => ({ target, purpose, name: `photo-${i + 1}.jpg`, blob: p.blob, path: p.path ?? (p.blob ? sender.pathOf(p.blob) : undefined), bytes: p.blob?.size ?? 0 }))
          .map((p) => (p.path ? { ...p, blob: undefined } : p));
      const r = await offline.keep({
        id: `QA-${session.idempotencyKey}`,
        kind: 'finish',
        appointmentId: target.appointmentId,
        unit: target.unit,
        day: target.serviceDateKey,
        label: `${t('today.finish')} · ${target.unit}`,
        endpoint: '/api/completions',
        body: { session, appointmentId: target.appointmentId, form: { ...form, language: lang, photos: [], unitCondition: form.unitCondition ? { ...form.unitCondition, photos: [] } : null } },
        photos: [...photoOf(visitNow.current, afterSender, 'form.photos', 'after'), ...(form.unitCondition ? photoOf(reportNow.current, reportSender, 'form.unitCondition.photos', 'unit-condition') : [])],
      });
      if (r === 'saved') {
        const m = t('crew1102.savedToast', { what: t('crew1102.what.finish', { unit: target.unit }) });
        toast(m);
        announce(m);
        onDone();
        return;
      }
      const m = r === 'full' ? t('crew1102.full', { mb: 40 }) : t('crew1102.storage');
      setError(m);
      announce(m, 'assertive');
    };
    try {
      // a press already waiting on this phone for this visit keeps its place: this one goes behind it
      if (keepable && (offline.heldFor(target.appointmentId).length > 0 || (typeof navigator !== 'undefined' && navigator.onLine === false))) {
        await keepOnPhone();
        return;
      }
      // 18 Oct 2026: a send already on its way finishes first; only what is still on the device counts as to send
      await Promise.all([afterSender.settled(), reportSender.settled()]);
      const unsent = (items: PhotoItem[] | null, sender: PhotoSender) => (items ?? []).filter((p) => !p.path && p.blob && !sender.pathOf(p.blob)).length;
      const total = unsent(visitNow.current, afterSender) + (form.unitCondition ? unsent(reportNow.current, reportSender) : 0);
      const after = await upload(visitNow, afterSender, 0, total, setVisitPhotos);
      const photos = after.paths;
      const rPhotos = form.unitCondition ? (await upload(reportNow, reportSender, after.sent, total, setReportPhotos)).paths : [];
      setBusy(t('finish.sending'));
      const body: CloseoutForm = { ...form, language: lang, photos, unitCondition: form.unitCondition ? { ...form.unitCondition, photos: rPhotos } : null };
      if (mode === 'edit' && prev) {
        const marker = await apiFetch<CloseoutMarker>(`/api/closeouts/${encodeURIComponent(prev.marker.transactionId)}/edit`, { method: 'POST', body: JSON.stringify({ form: body }) });
        // 1.6.7: nothing the report shows changed — no report was written; the sheet says so and closes
        const msg = marker.unchanged ? t('ba167.nothingChanged') : t('finish.saved', { detail: marker.editedBy ? t('finish.savedAfter', { v: marker.reportVersion }) : t('finish.savedInside') });
        toast(msg);
        announce(msg);
        onDone();
        return;
      }
      const res =
        mode === 'resubmit' && completionId
          ? await apiFetch<CloseoutSubmitResponse>(`/api/completions/${encodeURIComponent(completionId)}/operation`, { method: 'POST', body: JSON.stringify({ op: 'resubmit', session, form: body }) })
          : await apiFetch<CloseoutSubmitResponse>('/api/completions', { method: 'POST', body: JSON.stringify({ session, appointmentId: target.appointmentId, form: body }) });
      const label = mode === 'resubmit' ? t('review.label.resubmit', { unit: target.unit }) : `${t('today.finish')} · ${target.unit}`;
      tx.track(res, { label, unit: target.unit, appointmentId: target.appointmentId, dayKey: target.serviceDateKey });
      const msg =
        res.status === 'duplicate'
          ? t('tx.duplicate')
          : res.memos.some((m) => m.code === 'INT_UNIT_SAFETY_ALERT')
            ? t('finish.recordedFlag', { unit: target.unit, n: windowMin })
            : pressWords(t, res, t('finish.recorded', { unit: target.unit }), t('finish.recordedNow', { unit: target.unit }));
      toast(msg);
      announce(msg);
      onDone();
    } catch (e) {
      if (keepable && (isNetworkFailure(e) || (e instanceof PhotoNotSent && (lastPhotoFailure === 'network' || (typeof navigator !== 'undefined' && navigator.onLine === false))))) {
        try {
          await keepOnPhone();
        } finally {
          setBusy(null);
        }
        return;
      }
      // 17 Oct 2026: a photo that did not upload is said plainly — which one, nothing sent yet, the answers kept
      const m = e instanceof PhotoNotSent ? t('finish.photoUploadStopped', { n: e.n, button: recordLabel }) : e instanceof ApiRequestError ? (e.body?.message ?? e.message) : (e as Error).message;
      const keys = e instanceof ApiRequestError ? ((e.body as { keys?: string[] } | null)?.keys ?? []) : [];
      if (keys.length) setInvalid(keys.map((k) => ({ key: k, field: '' })));
      setError(m);
      announce(m, 'assertive');
    } finally {
      setBusy(null);
    }
  };

  const mgr = cfg.recipients.buildingManager;

  return (
    <SheetContent
      title={title}
      wide={report}
      returnFocusTo={returnFocusTo}
      data-testid="finish-sheet"
      footer={
        <>
          <Button variant="secondary" type="button" onClick={onDone}>
            {t('book.cancel')}
          </Button>
          <Button type="button" onClick={() => submit()} disabled={!!busy} doneLabel={t('busy1024.sent')} data-testid="finish-confirm">
            {busy ?? recordLabel}
          </Button>
        </>
      }
    >
      <div className="finish-sections">
        <p className="help" data-testid="finish-who">
          <b>{target.unit}</b> · {t('finish.visitLine', { time: target.time ?? formatDayKey(target.serviceDateKey, i18n.language), name: target.tenantName })}
        </p>
        {mode === 'resubmit' && returnNotes ? (
          <div className="warn" role="note">
            <Icon name="info" />
            <div>{t('finish.returnedNote', { notes: returnNotes })}</div>
          </div>
        ) : null}
        {mode === 'edit' ? (
          <div className="warn" role="note" data-testid="edit-note">
            <Icon name="info" />
            <div>{insideWindow ? t('finish.editInside', { n: windowMin }) : t('finish.editAfter')}</div>
          </div>
        ) : null}

        <fieldset className="field">
          <legend>{t('finish.roomsLabel')}</legend>
          <ul className="checklist finish-rooms" aria-label={t('finish.roomsLabel')}>
            {q.rooms
              .filter((r) => !r.hidden || r.key in form.rooms)
              .map((r) => (
                <li key={r.key}>
                  <input id={`room-${r.key}`} type="checkbox" checked={!!form.rooms[r.key]} onChange={(e) => set('rooms', { ...form.rooms, [r.key]: e.target.checked })} />
                  <label htmlFor={`room-${r.key}`}>{r.label[lang]}</label>
                  {r.help[lang] ? <WhatIs label={r.label[lang]}>{r.help[lang]}</WhatIs> : null}
                </li>
              ))}
          </ul>
          <p className="help">{t('finish.qFrom')}</p>
        </fieldset>

        <fieldset className="field">
          <legend>{t('finish.standard')}</legend>
          <div>
            {q.standard
              .filter((s) => !s.hidden || s.key in form.standard)
              .map((s) => (
                <YesNo
                  key={s.key}
                  id={`std-${s.key}`}
                  label={s.label[lang]}
                  value={!!form.standard[s.key]}
                  onChange={(v) => set('standard', { ...form.standard, [s.key]: v })}
                  help={s.help[lang] ? <WhatIs label={s.label[lang]}>{s.help[lang]}</WhatIs> : null}
                />
              ))}
          </div>
        </fieldset>

        <div className="grid-2">
          <div className="field">
            <label htmlFor="type-clean">{t('finish.typeClean')}</label>
            <select id="type-clean" className="input" value={form.typeOfClean} onChange={(e) => set('typeOfClean', e.target.value as CloseoutForm['typeOfClean'])} aria-describedby="type-clean-help">
              <option value="standard">{t('finish.tcStandard')}</option>
              <option value="deep">{t('finish.tcDeep')}</option>
            </select>
            <p className="help" id="type-clean-help">
              {form.typeOfClean === 'deep' ? t('finish.tcDeepHelp', { list: q.deepDefinition[lang] }) : t('finish.tcStandardHelp')}
            </p>
          </div>
          <div style={{ display: 'grid', gap: 12 }}>
            <div className="field">
              <label htmlFor="took">{t('finish.howLong')}</label>
              <select
                id="took"
                className="input"
                value={form.durationMinutes ?? ''}
                onChange={(e) => set('durationMinutes', e.target.value ? Number(e.target.value) : null)}
                aria-describedby="took-help"
              >
                {DURATION_STEPS.map((m) => (
                  <option key={m} value={m}>
                    {durationLabel(t, m)}
                  </option>
                ))}
              </select>
              <p className="help" id="took-help">
                {t('finish.howLongHelp')}
              </p>
            </div>
            <div className="field">
              <label htmlFor="workers">{t('finish.howMany')}</label>
              <select id="workers" className="input" value={form.workers} onChange={(e) => set('workers', Number(e.target.value))} aria-describedby="workers-help">
                {Array.from({ length: MAX_WORKERS }, (_, i) => i + 1).map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
              <p className="help" id="workers-help">
                {t('finish.howManyHelp')}
              </p>
            </div>
          </div>
        </div>

        <div className="field">
          <label htmlFor="outcome">{t('finish.outcome')}</label>
          <select id="outcome" className="input" value={form.outcome} onChange={(e) => set('outcome', e.target.value as CloseoutForm['outcome'])} aria-describedby="outcome-help">
            {SERVICE_OUTCOMES.map((o) => (
              <option key={o} value={o}>
                {t(`serviceOutcome.${o}`)}
              </option>
            ))}
          </select>
          <p className="help" id="outcome-help">
            {t('finish.outcomeHelp')}
          </p>
        </div>

        <fieldset className="field" id="fu-required-wrap">
          <legend id="level-h">{t('finish.level')}</legend>
          <p className="help">{report ? t('finish.levelFromReport') : t('finish.levelHelp')}</p>
          <div className="radio-cards" role="radiogroup" aria-labelledby="level-h">
            {CONDITION_LEVELS.map((l) => (
              <label key={l}>
                <input type="radio" name="level" value={l} checked={level === l} disabled={report} onChange={() => setLevel(l)} data-testid={`level-${l}`} />
                <span>
                  {t(`finish.levels.${l}`)}
                  <small>{t(`finish.levelHelps.${l}`)}</small>
                </span>
              </label>
            ))}
          </div>
          {!report && level === 'Elevated Cleaning Need' ? (
            <div className="grid-2" data-testid="elevated-follow-up">
              <label className="switch" style={{ gridColumn: '1 / -1' }}>
                <input id="fu-required" type="checkbox" checked={form.followUp.required} onChange={(e) => set('followUp', { ...form.followUp, required: e.target.checked })} />
                <span>{t('finish.followUpRequired')}</span>
              </label>
              <div className="field">
                <label htmlFor="fu-cat">{t('finish.followUpCategory')}</label>
                <select id="fu-cat" className="input" value={form.followUp.category} onChange={(e) => set('followUp', { ...form.followUp, category: e.target.value as CloseoutForm['followUp']['category'] })}>
                  {COMPLETION_FOLLOW_UP_CATEGORIES.map((c) => (
                    <option key={c} value={c}>
                      {t(`finish.fuCat.${c}`)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label htmlFor="fu-pri">{t('finish.followUpPriority')}</label>
                <select id="fu-pri" className="input" value={form.followUp.priority} onChange={(e) => set('followUp', { ...form.followUp, priority: e.target.value as CloseoutForm['followUp']['priority'] })}>
                  {PRIORITIES.map((p) => (
                    <option key={p} value={p}>
                      {t(`finish.report.fp.${p}`)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field" style={{ gridColumn: '1 / -1' }}>
                <label htmlFor="fu-sum">{t('finish.followUpSummary')}</label>
                <input id="fu-sum" className="input" value={form.followUp.summary} onChange={(e) => set('followUp', { ...form.followUp, summary: e.target.value })} />
              </div>
            </div>
          ) : null}
        </fieldset>

        {/* 1.6.7: the Before photos the Arrived step took, so the After photos can match the angle */}
        <BeforePhotos appointmentId={target.appointmentId} photos={cfg?.beforePhotos ?? []} />
        <PhotoPicker
          id="photo-hk"
          testid="visit-photos"
          items={visitPhotos}
          onChange={changeVisitPhotos}
          legend={t('arrived.afterPhotos')}
          pickedWords={() => t('finish.photosSending')}
          {...afterTrouble}
          help={
            <span data-testid="photo-release">
              {t('arrived.photosLine')}{' '}
              {release.onFile ? (release.date ? t('finish.photoRelease', { date: formatDayKey(release.date.replace(/-/g, ''), i18n.language) }) : t('finish.photoReleaseNoDate')) : t('finish.photoReleaseMissing')} ·{' '}
              {t('finish.photosMany')}
            </span>
          }
        />

        <div className="field">
          <label htmlFor="fnote">{t('finish.notes')}</label>
          <textarea id="fnote" className="input" placeholder={t('finish.notesPlaceholder')} value={form.notes} onChange={(e) => set('notes', e.target.value)} />
        </div>

        <div className="switch-block">
          <label className="switch">
            <input id="hk-incident" type="checkbox" checked={form.incident.on} onChange={(e) => set('incident', { ...form.incident, on: e.target.checked })} data-testid="switch-incident" />
            <span>
              <span>{t('finish.incident')}</span> — <span className="help">{t('finish.incidentHelp')}</span>
            </span>
          </label>
          {form.incident.on ? (
            <div className="field">
              <label htmlFor="hk-what">{t('finish.whatHappened')}</label>
              <textarea id="hk-what" className="input" required aria-required="true" value={form.incident.what} onChange={(e) => set('incident', { ...form.incident, what: e.target.value })} />
              <div className="route">
                <Icon name="mail" />
                <div>
                  <b>{t('finish.incidentMemo')}</b>
                  <br />
                  <span>{t('finish.incidentMemoBody', { n: windowMin })}</span>
                  {!cfg.recipients.incident.length ? (
                    <>
                      <br />
                      <span>{t('finish.incidentNotSet')}</span> <ChangeInSettings code="EMAIL_INCIDENT_RECIPIENTS" />
                    </>
                  ) : null}
                </div>
              </div>
            </div>
          ) : null}

          <label className="switch">
            <input id="unit-condition" type="checkbox" checked={report} onChange={(e) => setUnitCondition(e.target.checked)} data-testid="switch-unit-condition" />
            <span>
              <span>{t('finish.unitCond')}</span> <span className="help">{t('finish.unitCondHelp')}</span>
            </span>
          </label>
          {form.unitCondition ? (
            <IncidentReport value={form.unitCondition} onChange={(u) => set('unitCondition', u)} photos={reportPhotos} onPhotos={setReportPhotos} />
          ) : null}

          {mode === 'record' ? (
            <label className="switch">
              <input
                id="send-feedback"
                type="checkbox"
                disabled={!cfg.tenantHasEmail}
                checked={form.sendFeedback && cfg.tenantHasEmail}
                onChange={(e) => set('sendFeedback', e.target.checked)}
                aria-describedby="send-feedback-help"
              />
              <span>
                {t('finish.sendFeedback')}
                <span className="help" id="send-feedback-help" style={{ display: 'block' }}>
                  {cfg.tenantHasEmail ? t('finish.sendFeedbackHelp') : t('finish.sendFeedbackNoEmail')}
                </span>
              </span>
            </label>
          ) : null}
        </div>

        {report ? (
          <div className="route" data-testid="route-note">
            <Icon name="mail" />
            <div>
              <b>{t('finish.routeTitle')}</b>
              <br />
              <span>{t('finish.routeBody', { n: windowMin })}</span>
              <br />
              <span>{mgr.name || mgr.email ? t('finish.routeTo', { name: [mgr.name, mgr.email].filter(Boolean).join(' · ') }) : t('finish.recipientsNotSet')}</span>
              {mgr.name || mgr.email ? null : (
                <>
                  {' '}
                  <ChangeInSettings code="BUILDING_MANAGER_EMAIL" />
                </>
              )}
            </div>
          </div>
        ) : null}
        {report || form.incident.on ? (
          <p className="help" data-testid="draft-note">
            {cfg.createdSettings.length ? `${t('finish.createdSettings', { keys: cfg.createdSettings.join(', ') })} ` : ''}
            {t('finish.draftNote')}
          </p>
        ) : null}

        {invalid.length ? (
          <div className="warn take" role="alert" tabIndex={-1} ref={problems} data-testid="finish-invalid">
            <Icon name="alert" />
            <div>
              <b>{t('finish.fixThese')}</b>
              <ul className="invalid-list">
                {invalid.map((p) => (
                  <li key={p.key}>
                    {p.field ? (
                      <a
                        href={`#${p.field}`}
                        onClick={(e) => {
                          e.preventDefault();
                          const el = document.getElementById(p.field);
                          const control = el && !el.matches('input,select,textarea,button') ? el.querySelector<HTMLElement>('button,input,select,textarea') : el;
                          (control ?? el)?.focus();
                        }}
                      >
                        {t(`finish.invalid.${p.key}`)}
                      </a>
                    ) : (
                      t(`finish.invalid.${p.key}`)
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ) : null}
        {error && !invalid.length ? (
          <div className="warn take" role="alert" data-testid="finish-error">
            <Icon name="alert" />
            <div>{error}</div>
          </div>
        ) : null}
      </div>
    </SheetContent>
  );
}
